import { useCallback, useEffect, useState } from "react";
import { DownloadSimple, SealCheck, Stack } from "@phosphor-icons/react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { BUCKET_TONE, cr, pct } from "@/lib/wealth";
import { EmptyState } from "@/components/shared";
import { toast } from "sonner";

const delta = (a, b) => (a == null || b == null ? null : Math.round((a - b) * 100) / 100);

export default function WealthPositions() {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState([]);   // ids, max 2

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: d } = await api.get(`/tenants/${tenantId}/wealth/positions`);
      setData(d);
      setCompare(d.positions.slice(0, 2).map((p) => p.id));
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    const note = window.prompt("Note for this approved wealth position (optional)") ?? "";
    try {
      const { data: res } = await api.post(`/tenants/${tenantId}/wealth/positions`, { note });
      toast.success(`${res.version_label} approved and frozen`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleCompare = (id) =>
    setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id)
      : [...c, id].slice(-2)));

  const handout = (positionId) =>
    window.open(fileUrl(`/tenants/${tenantId}/wealth/handout${positionId ? `?position_id=${positionId}` : ""}`), "_blank");

  const positions = data?.positions || [];
  const [aId, bId] = compare;
  const a = positions.find((p) => p.id === aId);
  const b = positions.find((p) => p.id === bId);

  return (
    <WealthShell
      title="Board positions & handout"
      description="Freeze the current position as an approved Family Wealth Position, compare any review with the last one, and download the one-page handout to leave behind after the meeting."
      actions={
        <div className="flex flex-wrap gap-2">
          <button onClick={() => handout(null)} data-testid="handout-current"
                  className="inline-flex items-center gap-2 rounded-full border border-plum/40 px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-plum hover:bg-plum hover:text-white">
            <DownloadSimple size={14} /> Current handout (PDF)
          </button>
          {data?.can_approve && (
            <button onClick={approve} data-testid="approve-position"
                    className="inline-flex items-center gap-2 rounded-full bg-plum px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              <SealCheck size={14} /> Approve current position
            </button>
          )}
        </div>
      }
    >
      {positions.length === 0 ? (
        <EmptyState testid="positions-empty" title="No approved positions yet"
                    message="Approve the current position to freeze it — every approved version is kept for the Board to compare later." />
      ) : (
        <div className="space-y-6" data-testid="wealth-positions">
          <div className="panel overflow-x-auto" data-testid="positions-list">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  {["", "Version", "Statement date", "Net worth", "Safety %", "Market %",
                    "Aspirational %", "Approved by", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {positions.map((p) => (
                  <tr key={p.id} className={compare.includes(p.id) ? "bg-plum/5" : ""}
                      data-testid={`position-row-${p.version}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={compare.includes(p.id)}
                             data-testid={`position-compare-${p.version}`}
                             onChange={() => toggleCompare(p.id)} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">{p.version_label}</p>
                      {p.note && <p className="text-[11px] text-muted-foreground">{p.note}</p>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-foreground/80">{(p.statement_date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-navy">{cr(p.totals.net_worth)}</td>
                    {["safety", "market", "aspirational"].map((k) => (
                      <td key={k} className="px-4 py-3 text-right font-mono tabular-nums text-foreground/80">
                        {pct(p.buckets.find((x) => x.bucket === k)?.current_pct)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-[11px] text-muted-foreground">
                      {p.approved_by_email}<br />{(p.approved_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handout(p.id)} data-testid={`handout-${p.version}`}
                              className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-plum"
                              title="Download this version's handout">
                        <DownloadSimple size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {a && b && a.id !== b.id && (
            <div className="panel overflow-x-auto" data-testid="positions-compare">
              <div className="flex items-center gap-2 border-b border-border px-6 py-4">
                <Stack size={18} className="text-plum" />
                <h3 className="font-display text-lg text-navy">
                  {b.version_label} → {a.version_label}
                </h3>
              </div>
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left">
                    {["Measure", b.version_label, a.version_label, "Change"].map((h) => (
                      <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr data-testid="compare-net-worth">
                    <td className="px-4 py-3 font-medium text-navy">Total family net worth</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{cr(b.totals.net_worth)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{cr(a.totals.net_worth)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${
                      a.totals.net_worth >= b.totals.net_worth ? "text-success" : "text-destructive"}`}>
                      {cr(a.totals.net_worth - b.totals.net_worth)}
                    </td>
                  </tr>
                  {["safety", "market", "aspirational"].map((k) => {
                    const ba = a.buckets.find((x) => x.bucket === k);
                    const bb = b.buckets.find((x) => x.bucket === k);
                    const d = delta(ba?.current_pct, bb?.current_pct);
                    return (
                      <tr key={k} data-testid={`compare-${k}`}>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${BUCKET_TONE[k].chip}`}>
                            {ba?.label || k}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">{pct(bb?.current_pct)}</td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">{pct(ba?.current_pct)}</td>
                        <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${
                          d == null ? "text-muted-foreground" : d > 0 ? "text-success" : d < 0 ? "text-destructive" : ""}`}>
                          {d == null ? "—" : `${d > 0 ? "+" : ""}${d.toFixed(2)}%`}
                        </td>
                      </tr>
                    );
                  })}
                  <tr data-testid="compare-accessible">
                    <td className="px-4 py-3 font-medium text-navy">Accessible ≤ 90 days</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{pct(b.accessible_90_days?.pct)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{pct(a.accessible_90_days?.pct)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums">
                      {(() => {
                        const d = delta(a.accessible_90_days?.pct, b.accessible_90_days?.pct);
                        return d == null ? "—" : `${d > 0 ? "+" : ""}${d.toFixed(2)}%`;
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="px-6 py-3 text-[11px] text-muted-foreground">
                Approved positions are frozen — later price edits or configuration changes never alter them.
              </p>
            </div>
          )}
        </div>
      )}
    </WealthShell>
  );
}
